'use client'

import { useEffect, useState, useCallback, useMemo } from 'react';
import Cookies from 'js-cookie';
import short from 'short-uuid';
import { Profile } from '../models';

const EMPTY_PROFILE: Profile = {
    id: '', userId: '', email: '', username: '', firstName: '',
    lastName: '', bonusTotal: 0, summary: '', funFact: '', phone: '',
    gender: '', status: '', link: '', qrCode: '', profilePicture: ''
};

// Helper function to check if an object is the empty profile
const isProfileEmpty = (profile: Profile) => {
    return !profile || !profile.id;
};

export const useAccountContext:any = () => {
    // --- State Initialization using Lazy Initializers (This is already excellent) ---

    const [signedIn, setSignedIn] = useState<boolean>(() => {
        if (typeof window !== 'undefined') {
            return window.localStorage.getItem("signedIn") === 'true';
        }
        return false;
    });

    const [profile, setProfile] = useState<Profile>(() => {
        if (typeof window !== 'undefined') {
            try {
                const storedProfile = window.sessionStorage.getItem("profile");
                return storedProfile ? JSON.parse(storedProfile) : EMPTY_PROFILE;
            } catch (error) {
                console.error("Failed to parse profile from sessionStorage", error);
                return EMPTY_PROFILE;
            }
        }
        return EMPTY_PROFILE;
    });

    const [deviceId, setDeviceId] = useState<string>(() => {
        if (typeof window !== 'undefined') {
            return Cookies.get("deviceId") || '';
        }
        return '';
    });

    const [frontSessionId, setFrontSessionId] = useState<string>(() => {
        if (typeof window !== 'undefined') {
            return Cookies.get("frontSessionId") || '';
        }
        return '';
    });


    // --- Persistence Effects (Slightly Refined) ---

    // Persist `signedIn` state to localStorage
    useEffect(() => {
        window.localStorage.setItem("signedIn", String(signedIn));
    }, [signedIn]);


    // Persist `profile` state to sessionStorage, but only if it's not empty
    useEffect(() => {
        // console.log('profile1: ', profile)
        if (!isProfileEmpty(profile)) {
            window.sessionStorage.setItem("profile", JSON.stringify(profile));
        } else {
            // If the profile is cleared, remove it from storage to keep things clean
            window.sessionStorage.removeItem("profile");
        }
    }, [profile]);

    // Consolidate cookie management into a single effect for clarity
    useEffect(() => {
        // Handle deviceId
        if (deviceId) {
            Cookies.set("deviceId", deviceId, { expires: 365, sameSite: 'strict' });
        }
        
        // Handle frontSessionId
        if (!frontSessionId) {
            const newSessionId = short.generate();
            Cookies.set("frontSessionId", newSessionId, { sameSite: 'strict' });
            setFrontSessionId(newSessionId); // Update state to reflect the new ID
        }
    }, [deviceId, frontSessionId]);


    // --- Action: Add a consolidated "logout" or "reset" function ---
    const resetAccountContext = useCallback(() => {
        // Update state to defaults
        setSignedIn(false);
        setProfile(EMPTY_PROFILE);
        // We typically keep deviceId and frontSessionId on logout

        // Clear storage
        window.localStorage.removeItem("signedIn");
        window.sessionStorage.removeItem("profile");
        // You might also want to clear other related storage items here

        console.log("Account context has been reset.");
    }, []); 

    // Memoize the return value so the object itself is stable
    const contextValue = useMemo(() => ({
        signedIn, 
        setSignedIn, 
        frontSessionId, 
        setFrontSessionId, 
        deviceId, 
        setDeviceId, 
        profile, 
        setProfile,
        resetAccountContext 
    }), [
        signedIn, setSignedIn, frontSessionId, setFrontSessionId, 
        deviceId, setDeviceId, profile, setProfile, resetAccountContext
    ]);


    return contextValue;
}

export type AccountContextValue = ReturnType<typeof useAccountContext>;